import { IsoDate, IsoDateTime, IsoDateTimeTz, IsoDuration } from '../../../types/iso-date';
import { deconstructDate } from '../../helper/deconstruct-date.function';
import { constructDate } from '../../helper/construct-date.function';

/**
 * Add a number of quarters (3-month increments) to an ISO date/date-time string while preserving its shape.
 *
 * Supported inputs:
 * - Date-only: YYYY-MM-DD -> returns YYYY-MM-DD
 * - Date-time (no tz): YYYY-MM-DDTHH:mm:ss(.fraction[1-6])? -> returns same shape
 * - Date-time with tz: YYYY-MM-DDTHH:mm:ss(.fraction[1-6])?(Z|±HH:MM) -> returns same shape, preserving the original tz literal
 *
 * Rules:
 * - Only the calendar date portion is changed; time-of-day and fractional seconds are preserved verbatim.
 * - Day is clamped to the last valid day of the target month (e.g., Jan 31 + 1 quarter -> Apr 30).
 * - `amount` must be a finite integer (can be negative).
 */
export function addQuarters<T extends IsoDate | IsoDateTime | IsoDateTimeTz | IsoDuration>(input: T, amount: number): T {
  if(!Number.isInteger(amount) || !isFinite(amount))
    throw new Error('addQuarters: amount must be a finite integer');
  if(amount === 0)
    return input;

  const deconstructed = deconstructDate(input);
  if(deconstructed._type !== 'date'
    && deconstructed._type !== 'dateTime'
    && deconstructed._type !== 'dateTimeTz'
    && deconstructed._type !== 'duration') {
    throw new Error('addDays: invalid ISO date/time/duration string');
  }
  if(deconstructed._type === 'duration') {
    const totalMonths = (deconstructed.years ?? 0) * 12 + (deconstructed.months ?? 0) + amount * 3;
    if(totalMonths < 0)
      throw new Error('addMonths: resulting duration would be negative');
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    deconstructed.years = years;
    deconstructed.months = months;
    return constructDate(deconstructed) as unknown as T;
  }

  const {year, month, day} = deconstructed;

  const base = year * 12 + (month - 1) + amount * 3;
  const targetYear = Math.floor(base / 12);
  const targetMonthZero = base - targetYear * 12; // 0-11
  const targetMonth = targetMonthZero + 1; // 1-12

  const lastDay = new Date(Date.UTC(targetYear, targetMonth, 0)).getUTCDate();
  const targetDay = Math.min(day, lastDay);

  const dt = new Date(Date.UTC(targetYear, targetMonth - 1, targetDay));
  deconstructed.year = dt.getUTCFullYear();
  deconstructed.month = dt.getUTCMonth() + 1;
  deconstructed.day = dt.getUTCDate();
  return constructDate(deconstructed) as unknown as T;
}
